import { createNoopTransport } from "#bridge/transports/noop";
import { createPortTransport } from "#bridge/transports/port";
import type { BridgeTransport, SdkHint } from "#bridge/types";
import { SDK_CHANNEL_OBJECT, SDK_GLOBAL_HINT } from "#version";

type InjectedChannelObject = {
	postMessage: (data: string) => void;
	addEventListener?: (type: "message", listener: (event: MessageEvent) => void) => void;
	removeEventListener?: (type: "message", listener: (event: MessageEvent) => void) => void;
	onmessage?: ((event: MessageEvent) => void) | null;
};

declare global {
	interface Window {
		__INNO_SDK__?: SdkHint;
		/** Low-level WebView channel (native inject). Not `window.InnoSdk`. */
		innoSdkChannel?: InjectedChannelObject;
	}
}

export type DetectTransportOptions = {
	allowedOrigins?: readonly string[];
	forceNoop?: boolean;
	/** @see PortTransportOptions.outboundQueueTtlMs */
	outboundQueueTtlMs?: number;
};

/**
 * Default web transport:
 * - listens for TWA / transferred MessagePort via `window` `message`
 * - binds `window.innoSdkChannel` (WebMessageListener) when present
 */
export function createDefaultTransport(options: DetectTransportOptions = {}): BridgeTransport {
	if (options.forceNoop || typeof window === "undefined") {
		return createNoopTransport();
	}

	const transport = createPortTransport({
		allowedOrigins: options.allowedOrigins,
		outboundQueueTtlMs: options.outboundQueueTtlMs,
	});

	const channel = window[SDK_CHANNEL_OBJECT] as InjectedChannelObject | undefined;
	if (channel && typeof channel.postMessage === "function") {
		transport.bindPort(channel);
	}

	return transport;
}

export function getSdkHint(): SdkHint | undefined {
	if (typeof window === "undefined") return undefined;
	return window[SDK_GLOBAL_HINT];
}
